// 📄 Fichier : src/utils/dimensionEnricher.js
// Utilitaire pour enrichir les réponses avec les informations de dimension

/**
 * Enrichit les réponses avec les informations de dimension provenant du test
 * @param {Array} responses - Tableau des réponses à enrichir
 * @param {Object} testData - Données du test contenant les éléments et dimensions
 * @returns {Array} Réponses enrichies avec les dimensions
 */
export function enrichResponsesWithDimensions(responses, testData) {
  if (!responses || !Array.isArray(responses)) {
    console.warn('📊 [DimensionEnricher] Aucune réponse à enrichir');
    return responses || [];
  }

  if (!testData) {
    console.warn('📊 [DimensionEnricher] Aucune donnée de test fournie');
    return responses;
  }

  console.log(`📊 [DimensionEnricher] Enrichissement: "${testData.title || 'Sans titre'}" - ${responses.length} réponses, ${testData.dimensions?.length || 0} dimensions`);

  // Créer une map des éléments pour un accès rapide par ID
  const elementsMap = createElementsMap(testData);

  // Enrichir chaque réponse avec sa dimension
  const enrichedResponses = responses.map((response) => {
    try {
      const element = elementsMap[response.questionId];

      if (!element) {
        console.warn(`📊 [DimensionEnricher] Élément non trouvé pour questionId: ${response.questionId}`);
        return {
          ...response,
          dimension: null,
          dimensionInfo: null
        };
      }

      // Récupérer les informations de dimension
      const dimensionInfo = getDimensionInfo(element, testData, response.questionId);

      return {
        ...response,
        dimension: dimensionInfo?.id || null,
        dimensionInfo: dimensionInfo
      };
    } catch (error) {
      console.error(`📊 [DimensionEnricher] Erreur enrichissement ${response.questionId}:`, error);
      return {
        ...response,
        dimension: null,
        dimensionInfo: null
      };
    }
  });

  console.log('✅ [DimensionEnricher] Enrichissement terminé:', {
    total: enrichedResponses.length,
    withDimensions: enrichedResponses.filter(r => r.dimension).length,
    withoutDimensions: enrichedResponses.filter(r => !r.dimension).length
  });

  return enrichedResponses;
}

/**
 * Crée une map des éléments du test pour un accès rapide par ID
 * @param {Object} testData - Données du test
 * @returns {Object} Map des éléments indexés par ID
 */
function createElementsMap(testData) {
  const elementsMap = {};

  // Parcourir les éléments directs du test
  if (testData.elements && Array.isArray(testData.elements)) {
    testData.elements.forEach((element) => {
      const elementId = element.el_ID || element.id;
      if (elementId) {
        elementsMap[elementId] = element;
      }
    });
  }

  // Parcourir les pages pour les éléments imbriqués
  if (testData.pages && Array.isArray(testData.pages)) {
    testData.pages.forEach((page) => {
      if (page.children && Array.isArray(page.children)) {
        page.children.forEach((child) => {
          const childId = child.el_ID || child.id;
          if (childId) {
            // Rechercher l'élément complet dans testData.elements
            const fullElement = testData.elements?.find(el => (el.el_ID || el.id) === childId);
            if (fullElement) {
              elementsMap[childId] = fullElement;
            } else {
              elementsMap[childId] = child; // Fallback sur les données partielles
            }
          }
        });
      }
    });
  }

  console.log('📊 [DimensionEnricher] Map créée:', Object.keys(elementsMap).length, 'éléments');
  return elementsMap;
}

/**
 * Récupère les informations de dimension pour un élément donné
 * @param {Object} element - Élément du test
 * @param {Object} testData - Données du test
 * @param {string} questionId - ID de la question pour debug
 * @returns {Object|null} Informations de dimension ou null si non trouvée
 */
function getDimensionInfo(element, testData, questionId) {
  // Vérifier la dimension au niveau de l'élément
  if (element.dimension && Array.isArray(element.dimension) && element.dimension.length > 0) {
    const dimensionRef = element.dimension[0]; // Prendre la première dimension

    // Chercher la dimension complète dans les dimensions du test
    if (testData.dimensions && Array.isArray(testData.dimensions)) {
      const fullDimension = testData.dimensions.find(d =>
        d._id === dimensionRef.id ||
        d._id === dimensionRef._id ||
        d.label === dimensionRef.label
      );

      if (fullDimension) {
        return {
          id: fullDimension._id,
          label: fullDimension.label || dimensionRef.label,
          description: fullDimension.description || dimensionRef.description,
          color: fullDimension.color || '#6c757d'
        };
      }
    }

    // Fallback avec les données de l'élément si dimension complète non trouvée
    return {
      id: dimensionRef.id || dimensionRef._id,
      label: dimensionRef.label,
      description: dimensionRef.description,
      color: '#6c757d' // Couleur par défaut
    };
  }

  // Ne plus assigner de dimension par défaut - retourner null
  console.warn(`📊 [DimensionEnricher] Aucune dimension pour élément "${element.el_ID || element.id}"`);
  return null;
}

/**
 * Valide les réponses enrichies pour s'assurer de la cohérence
 * @param {Array} enrichedResponses - Réponses enrichies
 * @returns {Object} Rapport de validation
 */
export function validateEnrichedResponses(enrichedResponses) {
  if (!enrichedResponses || !Array.isArray(enrichedResponses)) {
    return {
      isValid: false,
      errors: ['Réponses enrichies invalides ou manquantes']
    };
  }

  const validation = {
    isValid: true,
    errors: [],
    warnings: [],
    statistics: {
      total: enrichedResponses.length,
      withDimensions: 0,
      withoutDimensions: 0,
      uniqueDimensions: new Set()
    }
  };

  enrichedResponses.forEach((response, index) => {
    // Vérifier la structure de base
    if (!response.questionId) {
      validation.errors.push(`Réponse ${index}: questionId manquant`);
    }

    if (!response.answered) {
      validation.warnings.push(`Réponse ${index}: aucune réponse fournie`);
    }

    // Statistiques des dimensions
    if (response.dimension && response.dimensionInfo) {
      validation.statistics.withDimensions++;
      validation.statistics.uniqueDimensions.add(response.dimension);
    } else {
      validation.statistics.withoutDimensions++;
      validation.warnings.push(`Réponse ${index} (${response.questionId}): aucune dimension associée`);
    }
  });

  validation.isValid = validation.errors.length === 0;
  validation.statistics.uniqueDimensions = Array.from(validation.statistics.uniqueDimensions);

  console.log('📊 [DimensionEnricher] Validation des réponses enrichies:', validation);
  return validation;
}

/**
 * Nettoie les réponses enrichies pour la sérialisation en base de données
 * @param {Array} enrichedResponses - Réponses enrichies
 * @returns {Array} Réponses optimisées pour la base de données
 */
export function cleanEnrichedResponsesForDatabase(enrichedResponses) {
  return enrichedResponses.map(response => ({
    ...response,
    // Conserver seulement les informations essentielles de la dimension
    dimensionInfo: response.dimensionInfo ? {
      id: response.dimensionInfo.id,
      label: response.dimensionInfo.label,
      color: response.dimensionInfo.color
    } : null
  }));
}